import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import { documentService, windowService } from '../../../../../../utils/window.utils';
import { Breakpoints } from '../../../../../../constants/breakpoints.constants';
import Icon from '../../../../../@shared/icon';
import { iconSize } from '../../../../../../constants/icon.constants';
import ctaTypes from '../../../../../../enums/CTA.enums';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import useAnimationEnd from '../../../../../../hooks/useUnMountOnAnimationEnd';
import {
  TDealerFilterComponentProps,
  TDealerSelectedFilterKeyName,
  TSelectedFilterOptions,
} from '../../../../types/dealerLocatorLanding.types';
import './styles.scss';

export type TDroplistProps = Partial<{
  droplistOptions: {
    Code?: string;
    Title?: JSX.Element | string;
    TitleUrl?: string;
    codeList?: Array<string>;
  }[];
  droplistTitle: string | JSX.Element;
  extraClassName: string;
  optionColumnCount: number;
}> & {
  filterKeyName: TDealerSelectedFilterKeyName;
  selectedFilters: TDealerFilterComponentProps['selectedFilters'];
  updateFilters: TDealerFilterComponentProps['updateFilters'];
};

const Droplist: FC<TDroplistProps> = ({
  droplistOptions = [],
  droplistTitle,
  extraClassName = '',
  optionColumnCount = 1,
  filterKeyName,
  selectedFilters,
  updateFilters,
}) => {
  const [shouldShowDroplist, setShouldShowDroplist] = useState(false);
  const [shouldAlignRight, setShouldAlignRight] = useState(false);
  const droplistElmRef = useRef<HTMLDivElement>(null);
  const canShowDOM = useAnimationEnd(shouldShowDroplist, 300);
  const [localSelectedFilter, setLocalSelectedFilter] = useState<TSelectedFilterOptions>({});
  const showDisabledResetCTA = Object.keys(localSelectedFilter)?.length === 0;

  useEffect(() => {
    setLocalSelectedFilter({ ...selectedFilters[filterKeyName] });
  }, [shouldShowDroplist]);

  useEffect(() => {
    function closeDroplistOnOutsideClick(event): void {
      if (windowService()?.innerWidth < Breakpoints['lg']) return;
      if (!droplistElmRef?.current?.contains(event?.target)) {
        setShouldShowDroplist(false);
      }
    }
    const docElement = documentService();
    if (!docElement?.body) return;
    docElement.addEventListener('click', closeDroplistOnOutsideClick, false);
    return (): void => docElement.removeEventListener('click', closeDroplistOnOutsideClick, false);
  }, []);

  // Auto align if droplist overflows outside browser
  useEffect(() => {
    if (!droplistElmRef?.current) return;
    const { right } = droplistElmRef?.current?.getElementsByClassName('dealer-droplist-filter__panel')[0]?.getBoundingClientRect() || {};
    if (right && right > documentService().documentElement?.clientWidth) {
      setShouldAlignRight(true);
    } else {
      setShouldAlignRight(false);
    }
  }, [canShowDOM]);

  const applyFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      const newFilters = { ...selectedFilters };
      newFilters[filterKeyName] = { ...localSelectedFilter };
      updateFilters({ ...newFilters });
      setShouldShowDroplist(false);
    },
    [localSelectedFilter]
  );

  const resetFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      if (showDisabledResetCTA) return;
      const newFilters = { ...selectedFilters };
      newFilters[filterKeyName] = {};
      updateFilters({ ...newFilters });
      setLocalSelectedFilter({});
    },
    [localSelectedFilter]
  );

  const saveLocalSelectedFilter = (TitleUrl: string, codeList: Array<string>, isChecked) => {
    const localSelectedFilterCopy = localSelectedFilter;
    if (isChecked) {
      delete localSelectedFilterCopy[TitleUrl];
    } else {
      localSelectedFilterCopy[TitleUrl] = codeList;
    }
    setLocalSelectedFilter({ ...localSelectedFilterCopy });
  };

  if (!droplistOptions.length) return <></>;

  const panelStyles = {
    '--option-column-count': optionColumnCount,
  } as React.CSSProperties;

  return (
    <div
      className={'dealer-droplist-filter' + (extraClassName ? ` ${extraClassName}` : '')}
      ref={droplistElmRef}
      onClick={(event) => {
        event.stopPropagation();
        setShouldShowDroplist(!shouldShowDroplist);
      }}
    >
      <span className="dealer-droplist-filter__title">{droplistTitle}</span>
      <Icon
        iconName={`arrow-chevron-${shouldShowDroplist ? 'up' : 'down'}`}
        size={iconSize['dsk']}
        extraClassName="dealer-droplist-filter__icon"
      />
      {canShowDOM && (
        <div
          className={`dealer-droplist-filter__panel 
            ${shouldShowDroplist ? 'dealer-droplist-filter__panel--open' : 'dealer-droplist-filter__panel--closed'}
            ${shouldAlignRight ? 'dealer-droplist-filter__panel--right' : ''}
          `}
          style={panelStyles}
        >
          <ul className="dealer-droplist-filter__option-wrapper">
            {droplistOptions?.map(({ TitleUrl = '', Title = '', codeList }) => {
              const isChecked = localSelectedFilter[TitleUrl];

              return (
                <li
                  className="dealer-droplist-filter__option"
                  onClick={(event) => {
                    event.stopPropagation();
                    codeList && saveLocalSelectedFilter(TitleUrl, codeList, isChecked);
                  }}
                >
                  <Icon iconName={isChecked ? 'action-checkbox-checked' : 'action-checkbox-default'} size={iconSize['dsk']} />
                  <span>{Title}</span>
                </li>
              );
            })}
          </ul>
          <div className="dealer-droplist-filter__cta-wrapper">
            <button
              onClick={resetFilter}
              className={`dealer-droplist-filter__cta--reset-filter
              ${ctaTypes['baseButton']} ${ctaTypes['primaryWhite']} ${ctaTypes['smallButton']} ${
                showDisabledResetCTA ? ' dealer-droplist-filter__cta--disabled' : ''
              }`}
            >
              <DictionaryTerm keyword="cnhi-dealerLocatorResetFilter" />
            </button>
            <button
              onClick={applyFilter}
              className={`${ctaTypes['baseButton']} ${ctaTypes['primaryRed']} ${ctaTypes['smallButton']} dealer-droplist-filter__cta--apply-filters`}
            >
              <DictionaryTerm keyword="cnhi-dealerLocatorApplyFilter" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Droplist;
